import { ErrorOverlay } from "@/components/overlay/error-overlay";
import {
  PublicAppStore,
  useAppStore,
  useInitializeAppStore,
  useInitializeGuestAppStore,
  usePublicAppStore,
} from "@/store";
import { QuickSearchModal } from "@/store/pages/SearchPageStore/QuickSearchModal/QuickSearchModal";
import { AppStoreContext, GuestAppStoreContext } from "@/store/utils/context";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { localStorageModule } from "@/modules/local-storage";
import { Outlet } from "react-router";
import { LoadingOverlay } from "@/components/overlay";
import { SyncStoreInitializationChecker } from "@/app/overlays/SyncStoreInitializationChecker";
import { BootstrapOverlay } from "@/app/overlays/BootstrapOverlay";
import { LogoutOverlay } from "@/app/overlays/LogoutOverlay";
import { UpgradedUserEmailChecker } from "@/app/overlays/UpgradedUserEmailChecker";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { UpsellModal } from "@/components/modal/upsell";
import { WelcomeToProModal } from "@/components/modal/welcome-to-pro/welcome-to-pro";
import {
  clearImportSessionInProgress,
  readImportSessionsInProgress,
} from "@/modules/cache/import-session";
import { trackConversionWithRewardful } from "@/modules/rewardful";
import { ThemeType, useTheme } from "@/mds";
import { removeQueryParams } from "@/store/navigation/helpers";
import { subscriptionModule } from "@/modules/subscriptions";
import { getAndClearPendingActions } from "@/app/router/handle-ephemeral-routes";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { OneClickImportModal } from "@/components/one-click-import/one-click-import-modal";
import { SmartViewsConvexProvider } from "@/smart-views/client/SmartViewsConvexProvider";
import { SmartViewUpdateNotifier } from "@/smart-views/components/SmartViewUpdateNotifier";

interface OutletLoadingHandlers {
  setIsReady: () => void;
  setIsError: () => void;
  setLoadingMessage: (message: string) => void;
}

interface StoreProviderOutletProps {}

export const StoreProviderOutlet: FC<StoreProviderOutletProps> = observer(() => {
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");

  const setIsReady = () => setShowLoadingOverlay(false);
  const setIsError = () => setShowErrorOverlay(true);

  return (
    <>
      <ErrorOverlay isVisible={showErrorOverlay} />
      <LoadingOverlay isVisible={showLoadingOverlay} loadingMessage={loadingMessage} />
      <StoreProviderOutletContent
        setIsReady={setIsReady}
        setIsError={setIsError}
        setLoadingMessage={setLoadingMessage}
      />
    </>
  );
});

export const StoreProviderOutletContent: FC<OutletLoadingHandlers> = observer(
  ({ setIsReady, setIsError, setLoadingMessage }) => {
    const { publicStore } = usePublicAppStore();

    if (publicStore.auth.isGuestMode) {
      return (
        <GuestModeStoreProviderOutlet
          publicStore={publicStore}
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      );
    }

    if (publicStore.auth.isStandardMode) {
      return (
        <StandardModeStoreProviderOutlet
          publicStore={publicStore}
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      );
    }

    /**
     * If the user is not in guest mode or standard mode, we
     * don't wrap the outlet in any store providers.
     */
    return <Outlet />;
  }
);

const GuestModeStoreProviderOutlet: FC<{ publicStore: PublicAppStore } & OutletLoadingHandlers> =
  observer(({ publicStore, setIsReady, setIsError }) => {
    const state = useInitializeGuestAppStore({
      publicAppStore: publicStore,
    });

    useEffect(() => {
      if (state.loading) {
        return;
      }

      if (state.error || !state.data) {
        setIsError();

        return;
      }

      setIsReady();
    }, [setIsError, setIsReady, state.data, state.error, state.loading]);

    if (!state.data) {
      return null;
    }

    return (
      <GuestAppStoreContext.Provider value={state.data}>
        <Outlet />
      </GuestAppStoreContext.Provider>
    );
  });

const StandardModeStoreProviderOutlet: FC<{ publicStore: PublicAppStore } & OutletLoadingHandlers> =
  observer(({ publicStore, setIsReady, setIsError, setLoadingMessage }) => {
    const state = useInitializeAppStore({
      publicAppStore: publicStore,
    });

    useEffect(() => {
      if (state.loading) {
        return;
      }

      if (state.error || !state.data) {
        setIsError();

        return;
      }

      /**
       * Note that we don't call `setIsReady` here because we also need
       * the StandardModeStoreProviderOutletContent to be ready.
       */
    }, [setIsError, setIsReady, state.data, state.error, state.loading]);

    if (!state.data) {
      return null;
    }

    return (
      <AppStoreContext.Provider value={state.data}>
        <StandardModeStoreProviderOutletContent
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      </AppStoreContext.Provider>
    );
  });

/**
 * We use this to kick-off global side effects in a hook.
 */
const StandardModeStoreProviderOutletContent: FC<OutletLoadingHandlers> = observer(
  ({ setIsReady, setIsError, setLoadingMessage }) => {
    const { store, pageStore } = useAppStore();
    const { themeType, setTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const isSyncInitializationFailed = store.sync.initializationFailed;

    store.useInitializeAppStoreEffects();
    pageStore.useInitializePageStoreEffects();

    // Handle pending ephemeral route actions (processed before router initialized)
    useEffectOnMount(() => {
      const processPendingActions = async () => {
        const pendingActions = await getAndClearPendingActions();
        for (const { config, data } of pendingActions) {
          config.handler(data, { store, pageStore });
        }
      };
      processPendingActions();
    });

    useEffect(() => {
      if (isSyncInitializationFailed) {
        // Keep only the sync initialization failure modal visible.
        setIsReady();
        return;
      }

      if (store.readyState.error) {
        setIsError();

        logger.error({
          message: "[StandardModeStoreProviderOutletContent] Error.",
          info: { error: objectModule.safeErrorAsJson(store.readyState.error) },
        });

        return;
      }

      if (store.readyState.loading) {
        return;
      }

      if (!store.readyState.data) {
        setIsError();

        logger.error({
          message: "[StandardModeStoreProviderOutletContent] Missing data.",
        });

        return;
      }

      setIsReady();
    }, [
      isSyncInitializationFailed,
      setIsError,
      setIsReady,
      store.readyState.data,
      store.readyState.error,
      store.readyState.loading,
    ]);

    // Coerce dark theme to light for free users after store is ready
    useEffect(() => {
      if (!store.readyState.data) return;
      const isAllowedDark = store.subscriptions.personalSubscription?.isPaid ?? false;

      if (themeType === ThemeType.HarvestMoon && !isAllowedDark) {
        setTheme(ThemeType.Harvest);
      }
    }, [
      setTheme,
      store.readyState.data,
      store.subscriptions.personalSubscription?.isPaid,
      themeType,
    ]);

    // Reconcile any import sessions left in-progress after a hard refresh
    useEffect(() => {
      if (!store.readyState.data) return;

      const cleanupInProgressImportSessions = async () => {
        const markers = await readImportSessionsInProgress();
        const sessionIds = new Set(markers.map(m => m.sessionId));

        for (const m of markers) {
          try {
            await store.sessions.updateSessionOnNewUserMessage(m.sessionId, { status: "ERROR" });
          } catch {
            // ignore per-session failure
          } finally {
            await clearImportSessionInProgress(m.sessionId);
          }
        }

        for (const sessionId of sessionIds) {
          store.sessionEvents.createMessageSentSessionEvent({
            session_id: sessionId,
            message_value: {
              kind: "REASONING",
              content: "Uploads were interrupted",
            },
          });

          trackEvent(TrackedEvent.ImportFilesBatchFailed, {
            session_id: sessionId,
            context: "StandardModeStoreProviderOutletContent",
          });
        }
      };

      cleanupInProgressImportSessions();
    }, [store.readyState.data, store.sessionEvents, store.sessions]);

    const bootstrapProgressMessage = store.sync.bootstrapProgressMessage;
    useEffect(() => {
      setLoadingMessage(bootstrapProgressMessage);
    }, [bootstrapProgressMessage, setLoadingMessage]);

    // Handle Stripe success/cancel params globally and strip them immediately
    useEffect(() => {
      const stripeParams = subscriptionModule.parseStripeQueryParams(location);

      if (stripeParams.isCheckoutSuccess) {
        store.platformInfo.setSubscriptionCompletedSuccessfully();
        store.platformInfo.openWelcomeToProModal();
        const email = store.account.myAccount.profileEmailAddress;
        if (email) trackConversionWithRewardful({ email });
        if (stripeParams.sessionId) {
          trackEvent(TrackedEvent.PaywallUpgradeReturn, {
            stripe_session_id: stripeParams.sessionId,
          });
          localStorageModule.writeStripeLastCheckoutSession(stripeParams.sessionId);
        }

        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      } else if (stripeParams.isCheckoutCancel) {
        trackEvent(TrackedEvent.PaywallCancelReturn, {});
        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      } else if (stripeParams.isBillingReturn && stripeParams.hasMemPro) {
        // We redirected them to billing portal because they were already paid;
        // optimistically show Pro status now that they're back
        store.platformInfo.setSubscriptionCompletedSuccessfully();

        // Refresh the platform info
        store.platformInfo.fetchPlatformInfo();

        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      } else if (stripeParams.isBillingReturn) {
        // Coming back from billing portal (manually opened, not auto-redirected)
        // Just refresh without optimistically showing Pro
        store.platformInfo.fetchPlatformInfo();

        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      }
    }, [location, navigate, store.platformInfo, store.account.myAccount.profileEmailAddress]);

    if (isSyncInitializationFailed) {
      return <SyncStoreInitializationChecker />;
    }

    if (!store.readyState.data) {
      return (
        <>
          <BootstrapOverlay />
        </>
      );
    }

    return (
      <>
        <SyncStoreInitializationChecker />
        <BootstrapOverlay />
        <LogoutOverlay />
        <UpgradedUserEmailChecker />
        <QuickSearchModal />
        <OneClickImportModal />
        {store.subscriptions.upsellModalIsOpen ? (
          <UpsellModal
            isOpen={store.subscriptions.upsellModalIsOpen}
            onClose={store.subscriptions.closeUpsell}
            gatedFeature={store.subscriptions.upsellModalFeature}
            level={store.subscriptions.upsellModalLevel}
          />
        ) : null}
        <WelcomeToProModal
          isOpen={store.platformInfo.welcomeToProModalIsOpen}
          onClose={store.platformInfo.closeWelcomeToProModal}
        />
        <SmartViewsConvexProvider>
          <SmartViewUpdateNotifier />
          <Outlet />
        </SmartViewsConvexProvider>
      </>
    );
  }
);
